import { useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, SectionHeading } from "@/components/motion";
import { LIBRARY, videoRecord } from "@/data/installVideos";

const STEPS = [
  { n: "01", t: "Lay out the frame", d: "Extend the telescoping bar to your banner width and use it as the measuring jig to mark both post positions." },
  { n: "02", t: "Drive the T-posts", d: "6'-6\" TP125/TP133 posts, driven with a T-post driver. Wear hearing protection and gloves. Keep them plumb." },
  { n: "03", t: "Fit the pole covers", d: "Slide the UV-protected black pole covers on and cap the tops. Clean look, and the bungees stop wearing on sharp post edges." },
  { n: "04", t: "Clip the bar", d: "T-clips connect the telescoping bar to each post. This is the patented moment — the posts can no longer fold inward under wind load." },
  { n: "05", t: "Bungee the banner", d: "7.5\" bungees along the top, 9.5\" down the sides. Tensioned with engineered give so gusts flex instead of tear." },
];


/** Branded cover facade — the Vimeo player only loads when the visitor clicks play,
 *  which keeps the page fast (no 11 iframes on load) and gives every video a branded cover. */
function VideoEmbed({ sec, v }) {
  const [playing, setPlaying] = useState(false);
  const cover = v.cover;
  return (
    <div data-testid={`vimeo-${sec.id}-${v.id}`}>
      <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">{v.label}</p>
      {playing ? (
        <div className="clip-corner overflow-hidden border border-girder bg-black">
          <div className="relative aspect-video">
            <iframe
              src={`https://player.vimeo.com/video/${v.id}?title=0&byline=0&portrait=0&autoplay=1`}
              className="absolute inset-0 h-full w-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={`${sec.heading}${v.label ? ` — ${v.label}` : ""}`}
            />
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setPlaying(true)} data-testid={`cover-${sec.id}-${v.id}`}
          aria-label={`Play ${sec.heading}${v.label ? ` — ${v.label}` : ""}`}
          className="clip-corner group relative block w-full overflow-hidden border border-girder bg-black">
          <div className="relative aspect-video">
            {cover ? (
              <>
                <img src={cover} alt="" loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                {v.playOverlay && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-hivis text-obsidian transition-transform duration-300 group-hover:scale-110">
                      <Play className="ml-1 h-7 w-7 fill-current" />
                    </span>
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="absolute inset-0 bg-steel/60" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-hivis text-obsidian transition-transform duration-300 group-hover:scale-110">
                    <Play className="ml-1 h-7 w-7 fill-current" />
                  </span>
                </span>
              </>
            )}
          </div>
        </button>
      )}
    </div>
  );
}

export default function InstallationPage() {
  return (
    <div data-testid="installation-page">
      <PageHeader
        kicker="Installation Videos"
        title="Fifteen minutes to windproof"
        sub="Watch the system go together, then follow the five-step sequence. Written instructions ship with every kit."
        image="/assets/img/lr-banner-bar-install-kit_orig.jpg"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <div className="mb-14 flex flex-wrap items-center gap-3" data-testid="video-jump-links">
          <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Jump to:</span>
          {LIBRARY.map((sec) => (
            <button key={sec.id} type="button" data-testid={`jump-${sec.id}`}
              onClick={() => document.getElementById(sec.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="clip-corner-sm border border-girder px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-300 transition-colors hover:border-hivis hover:text-hivis">
              {sec.jump}
            </button>
          ))}
        </div>
        <div className="space-y-10" data-testid="video-library">
          {LIBRARY.map((sec, si) => (
            <Reveal key={sec.id} i={si}>
              <div id={sec.id} className="clip-corner scroll-mt-28 border border-girder bg-steel p-6 sm:p-8" data-testid={`video-panel-${sec.id}`}>
                <h3 className="font-display text-3xl font-black uppercase tracking-tight text-slate-100 sm:text-4xl" data-testid={`video-section-${sec.id}`}>{sec.heading}</h3>
                <div className={`mt-8 grid gap-6 ${sec.videos.length === 1 ? "max-w-4xl" : sec.videos.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
                  {sec.videos.map((v) => (
                    <VideoEmbed key={v.id} sec={sec} v={videoRecord(v)} />
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 border-t border-girder pt-16">
          <SectionHeading kicker="The Sequence" title="Five steps" />
          <div className="mt-8 grid gap-x-10 md:grid-cols-2">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} i={i}>
                  <div className="flex gap-5 border-b border-girder py-5 first:pt-0" data-testid={`install-step-${s.n}`}>
                    <span className="text-outline font-display text-4xl font-black leading-none">{s.n}</span>
                    <div>
                      <h4 className="font-display text-lg font-bold uppercase text-slate-100">{s.t}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal i={5}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/tpost-guide" data-testid="install-tpost-link" className="clip-line-sm [--cb-line:#F59E0B] px-6 py-3 font-display font-bold uppercase text-hivis transition-colors hover:text-obsidian hover:[--cb-bg:#F59E0B]">Where to buy T-posts</Link>
                <Link to="/faqs" data-testid="install-faq-link" className="clip-line-sm [--cb-line:#1E293B] px-6 py-3 font-display font-bold uppercase text-slate-300 transition-colors hover:text-hivis hover:[--cb-line:#F59E0B]">Read the FAQs</Link>
              </div>
            </Reveal>
        </div>
      </section>
    </div>
  );
}
